import {
  BooleanField,
  CreateQuestionnaireFieldDto,
  DateField,
  DateTimeField,
  FieldInfoUnion,
  IntegerField,
  LabelField,
  LabelMode,
  ListDynamicField,
  ListDynamicSource,
  ListQuestionField,
  ListStaticField,
  OwnershipField,
  PredefinedRegEx,
  QuestionnaireFieldDto,
  QuestionnaireFieldIdentity,
  QuestionnaireFieldType,
  StringField,
  ValidationMode,
} from '@g2-shared/api-client/generated/onboarding-app';

export interface QuestionnaireFieldForm {
  questionnaireId: number;
  name: string;
  position: number;
  mandatory: boolean;
  hasComment: boolean;
  identity: QuestionnaireFieldIdentity;
  fieldInfo: FieldInfoUnion;
}

export const generateEmptyStringField = (): StringField => ({
  fieldType: 'STRING',
  length: 0,
  predefinedRegEx: null as unknown as PredefinedRegEx,
  regEx: '',
});

export const generateEmptyIntegerField = (): IntegerField => ({
  fieldType: 'NUMBER',
  minValue: 0,
  maxValue: 0,
  outsideRange: false,
});

export const generateEmptyDateField = (): DateField => ({
  fieldType: 'DATE',
  minDate: '',
  maxDate: '',
  outsideRange: false,
  validationMode: null as unknown as ValidationMode,
});

export const generateEmptyDateTimeField = (): DateTimeField => ({
  fieldType: 'DATE_TIME',
  minDateTime: '',
  maxDateTime: '',
  outsideRange: false,
  validationMode: null as unknown as ValidationMode,
});

export const generateEmptyListDynamicField = (): ListDynamicField => ({
  fieldType: 'DYNAMIC_LIST',
  source: ListDynamicSource.COUNTRY_LIST,
  allowOutOfList: false,
});

export const generateEmptyListStaticField = (): ListStaticField => ({
  fieldType: 'STATIC_LIST',
  items: [],
  allowOutOfList: false,
});

export const generateEmptyListQuestionField = (): ListQuestionField => ({
  fieldType: 'QUESTION_LIST',
  items: [],
  points: 0,
});

export const generateEmptyOwnershipField = (): OwnershipField => ({
  fieldType: 'OWNERSHIP',
  items: [],
  minValue: 0,
});

export const generateEmptyBooleanField = (): BooleanField => ({
  fieldType: 'BOOLEAN',
});

export const generateEmptyLabelField = (): LabelField => ({
  fieldType: 'LABEL',
  labelMode: LabelMode.TITLE,
  title: '',
  subTitle: '',
  description: '',
  collapsible: false,
});

export const generateFieldByFieldType = (type: QuestionnaireFieldType): FieldInfoUnion => {
  switch (type) {
    case QuestionnaireFieldType.NUMBER:
      return generateEmptyIntegerField();
    case QuestionnaireFieldType.OWNERSHIP:
      return generateEmptyOwnershipField();
    case QuestionnaireFieldType.BOOLEAN:
      return generateEmptyBooleanField();
    case QuestionnaireFieldType.LABEL:
      return generateEmptyLabelField();
    case QuestionnaireFieldType.QUESTION_LIST:
      return generateEmptyListQuestionField();
    case QuestionnaireFieldType.STATIC_LIST:
      return generateEmptyListStaticField();
    case QuestionnaireFieldType.STRING:
      return generateEmptyStringField();
    case QuestionnaireFieldType.DATE:
      return generateEmptyDateField();
    case QuestionnaireFieldType.DATE_TIME:
      return generateEmptyDateTimeField();
    case QuestionnaireFieldType.DYNAMIC_LIST:
      return generateEmptyListDynamicField();
  }
};

export const generateEmptyQuestionnaireFieldForm = (
  questionnaireId: number,
  autoProcessQuestionnaire?: boolean
): QuestionnaireFieldForm => ({
  questionnaireId,
  name: '',
  position: 0,
  mandatory: false,
  hasComment: false,
  identity: QuestionnaireFieldIdentity.none,
  fieldInfo: autoProcessQuestionnaire
    ? generateEmptyListQuestionField()
    : generateEmptyStringField(),
});

export const generateQuestionnaireFieldFormFromDto = (
  data: QuestionnaireFieldDto
): QuestionnaireFieldForm => {
  let predefinedRegEx: PredefinedRegEx;
  if (data.fieldInfo.fieldType === 'STRING') {
    predefinedRegEx = data.fieldInfo.predefinedRegEx
      ? data.fieldInfo.predefinedRegEx
      : (null as unknown as PredefinedRegEx);
    return { ...data, fieldInfo: { ...data.fieldInfo, predefinedRegEx } };
  }

  return { ...data };
};

export const generateCreateQuestionnaireFieldDto = (
  data: QuestionnaireFieldForm
): CreateQuestionnaireFieldDto => ({ ...data });
